import React, { useContext, useState } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
import {
  AppBar, Toolbar, Drawer, List, ListItemButton, useMediaQuery, useTheme, IconButton, Badge, Button,
} from "@mui/material";
import logo from "../assets/images/logo-round-purple.svg";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import { LanguageContext } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";
import { useSmartScroll } from "../hooks/useSmartScroll";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { t } from "../lib/localize";
import { FB } from "../content/fallbacks";
import { A11Y } from "../content/a11y";
import {
  color, up, down, focusOutline, focusOutlineOffset, easing,
  fontSize, fontWeight, hoverScale, brandTint,
} from "../styles/tokens";

// ─── Styled Components ───────────────────────────────────────────────────────

const StyledAppBar = styled(({ $hidden, $scrolled, ...props }) => <AppBar {...props} />)`
  && {
    background: ${color.white} !important;
    box-shadow: ${({ $scrolled }) => ($scrolled ? `0 6px 18px ${color.navShadow}` : "none")} !important;
    transform: translateY(${({ $hidden }) => ($hidden ? "-100%" : "0")});
    transition: transform 0.3s ${easing.standard}, box-shadow 0.2s ${easing.standard};
    will-change: transform;
    z-index: 1300;
  }

  @media (prefers-reduced-motion: reduce) {
    && { transition: none; }
  }
`;

const StyledToolbar = styled(({ ...props }) => <Toolbar {...props} />)`
  && {
    display: flex;
    justify-content: space-between;
    min-height: 75px !important;
    padding: 0 60px;
    user-select: none;

    ${down('mobile')} {
      padding: 0 30px;
      min-height: 60px !important;
    }
  }
`;

const NavLink = styled(Link)`
  font-size: ${fontSize.base};
  color: ${color.textNav};
  user-select: none;
  border-radius: 14px;
  transition: transform 0.3s ${easing.standard}, color 0.2s ${easing.standard};
  position: relative;

  /* Nav-specific focus ring: bumps outline-offset above the site default
     (from tokens) because horizontal nav text is small and tightly
     spaced — extra breathing room makes the ring read as intentional
     rather than cramped against neighboring links. CartLink /
     MobileCartLink below re-override to transfer the ring onto an
     inner element. */
  &:focus-visible {
    outline: ${focusOutline} !important;
    outline-offset: 7px;
    box-shadow: none !important;
  }

  &:hover { color: ${color.textPrimary}; transform: scale(${hoverScale.md}); }
  &:hover:active { transform: scale(1); }

  &[aria-current="page"]:not([href="/"]) {
    color: ${color.textPrimary};
    transform: scale(${hoverScale.md});
  }

  ${up('xl')} {
    &[aria-current="page"]:not([href="/"])::after {
      content: "";
      position: absolute;
      left: 50%;
      bottom: -0.7em;
      transform: translateX(-50%);
      width: 6px;
      height: 1.5px;
      background: currentColor;
      border-radius: 2px;
    }
  }
`;

const StyledLogo = styled.img`
  height: 31px;
  cursor: pointer;
  user-select: none;
  ${down('mobileLg')} { height: 23px; }
`;

const StyledIconButton = styled(IconButton)`
  && { color: ${color.brandPurple}; }
`;

const StyledBadge = styled(Badge)`
  & .MuiBadge-badge {
    background-color: ${color.textSecondary};
    color: ${color.white};
    font-size: 0.65rem;
    height: 16px;
    min-width: 18px;
  }
`;

/* ─── Mobile drawer ────────────────────────────────────────────────────────
   280px panel on the right with three sections:
     1. Header — brand logo (links back to /) + close button.
     2. Nav — each route as a list item with a left accent bar when active.
     3. Footer — language toggle, visually de-emphasized.
*/

const StyledDrawer = styled(Drawer)`
  && .MuiDrawer-paper {
    width: 280px;
    background: ${color.white};
    display: flex;
    flex-direction: column;
    /* Footer bottom inset only — the drawer's top matches the nav
       toolbar's top (neither applies safe-area-inset-top), so the
       drawer logo lines up with the nav logo at y=0 instead of
       being pushed down by the notch. */
    padding-bottom: env(safe-area-inset-bottom);
  }
`;

/* DrawerHeader matches the StyledToolbar's dimensions so the logo
   lives in the same spot whether the drawer is closed (logo in
   toolbar) or open (logo in drawer header). Left padding mirrors the
   toolbar exactly (60px / 30px) so the logo aligns; right padding is
   tighter (16px / 12px) so the close button sits near the drawer's
   right edge — the nav toolbar's menu button has the whole viewport
   to its right, but the drawer is only 280px wide, so the close
   button benefits from living closer to the edge. */
const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 0 60px;
  border-bottom: 1px solid ${color.borderLight};
  min-height: 75px;

  ${down('mobile')} {
    padding: 0 12px 0 30px;
    min-height: 60px;
  }
`;

const DrawerLogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  line-height: 0;
  border-radius: 8px;
`;

/* Responsive height matches StyledLogo in the toolbar, so the image
   size in the drawer equals the image size in the nav. */
const DrawerLogo = styled.img`
  height: 31px;
  user-select: none;
  ${down('mobileLg')} { height: 23px; }
`;

const DrawerCloseButton = styled(IconButton)`
  && {
    color: ${color.brandPurple};
    padding: 6px;
  }
`;

const DrawerNavList = styled(List)`
  && {
    flex: 1;
    padding: 8px 0;
  }
`;

/* Drawer items match the desktop NavLink interaction pattern — color
   shift + small scale on hover and active, no bg tint. transform-origin
   is left-center so the scale grows rightward into the drawer's padding
   instead of expanding both sides (which would feel springy in a
   vertical list). */
const DrawerItem = styled(ListItemButton)`
  && {
    font-size: ${fontSize.base};
    font-weight: ${fontWeight.medium};
    color: ${color.textNav};
    padding: 12px 24px;
    transform-origin: left center;
    transition: transform 0.3s ${easing.standard}, color 0.2s ${easing.standard};

    /* Full drawer-width row with transform-origin: left center. At md,
       the right edge travels ~8px; xs keeps the lift readable without
       the jump — consistent with the wide-row rule in tokens.js. */
    &:hover,
    &[aria-current="page"]:not([href="/"]) {
      color: ${color.textPrimary};
      transform: scale(${hoverScale.xs});
      background: transparent;
    }
  }
`;

const DrawerFooter = styled.div`
  border-top: 1px solid ${color.borderLight};
  padding: 4px 0;
`;

/* Language toggle shares the same motion vocabulary as DrawerItem —
   color shift + small scale, origin-left, no bg tint. Italic +
   textMuted default keeps it visually secondary to the nav list
   above. */
const DrawerLangButton = styled(ListItemButton)`
  && {
    color: ${color.textMuted};
    font-size: ${fontSize.base};
    font-style: italic;
    padding: 12px 24px;
    transform-origin: left center;
    transition: transform 0.3s ${easing.standard}, color 0.2s ${easing.standard};

    &:hover {
      color: ${color.textPrimary};
      transform: scale(${hoverScale.xs});
      background: transparent;
    }
  }
`;

const CartLink = styled(NavLink)`
  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  &:focus-visible ${() => CartButton} {
    outline: ${focusOutline};
    outline-offset: ${focusOutlineOffset};
    box-shadow: none;
  }
`;

const MobileCartLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  &:focus-visible > * {
    outline: ${focusOutline};
    outline-offset: ${focusOutlineOffset};
    box-shadow: none;
    border-radius: 50%;
  }
`;

const CartButton = styled(Button).attrs({ tabIndex: -1 })`
  && {
    background-color: ${color.surfaceMuted};
    /* textPrimary (pure black) at rest — confident, strong CTA
       presence distinct from the muted NavLinks. No color shift on
       hover; the scale + background tint carry the interactive
       feedback. */
    color: ${color.textPrimary};
    border: 1px solid ${color.borderFaint};
    padding: 6px 30px;
    border-radius: 30px;
    text-transform: none;
    font-size: ${fontSize.base};
    transition: transform 0.5s, background-color 0.2s ${easing.standard};
    &:hover {
      background-color: ${color.surfaceHoverMuted};
      transform: scale(${hoverScale.md});
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ $mobile }) => ($mobile ? "15px" : "70px")};
  align-items: center;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function Nav() {
  const { language, setLanguage } = useContext(LanguageContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { itemsInCartCount } = useCart();
  const { hidden, atTop } = useSmartScroll({ threshold: 8, downOffset: 80 });
  const settings = useSiteSettings();

  const toggleLanguage = () => {
    setLanguage((lang) => (lang === "EN" ? "FR" : "EN"));
    setDrawerOpen((v) => !v);
  };

  const handleDrawerToggle = () => setDrawerOpen((v) => !v);

  /* Used on drawer nav item clicks. Closes the drawer and explicitly
     drops focus from whatever is currently focused, deferred past
     MUI Drawer's focus-restore step.

     Without this, tapping a drawer item on mobile triggers:
       1. Drawer closes → MUI restores focus to the menu trigger button.
       2. Gatsby @reach/router navigates and runs its own focus
          management, landing focus on the first interactive element
          of the destination page.
       3. Browsers like Safari and Firefox treat that programmatic
          focus as keyboard-initiated and render the focus-visible
          ring on an element the user never tabbed to.

     Two rAFs defer the blur past both MUI's restore and @reach/router's
     focus step, so the new page loads with no lingering focus ring. */
  const handleNavClick = () => {
    setDrawerOpen(false);
    if (typeof window === 'undefined') return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.activeElement;
        if (el && typeof el.blur === 'function') el.blur();
      });
    });
  };

  /* Navigation comes from Site Settings, falling back to the shipped
     list when the document has not been authored yet.

     Which surface a link appears on is now its own `placement` field
     rather than its index. This previously depended on position: the
     drawer rendered slice(1), the desktop bar rendered slice(1, 6), and
     item 6 was never rendered from the array at all — the Inquiry
     button's label was hardcoded a second time further down. Adding or
     reordering a link silently pushed something out of the fixed
     slice(1, 6) window. With placement, array order controls order and
     nothing else. */
  const navItems = settings?.navItems?.length ? settings.navItems : FB.site.navItems;

  const label = (item) => t(item.label, language);

  /* Desktop link row: everything meant for the bar, except the CTA —
     that renders separately as the pill button on the far right. */
  const headerItems = navItems.filter(
    (i) => i.placement === 'both' || i.placement === 'header'
  );

  /* Mobile drawer: everything except header-only and hidden. The CTA is
     included because it appeared in the drawer before this change too —
     the mobile top bar shows it as an icon, and the drawer repeats it as
     a labelled row. */
  const drawerItems = navItems.filter(
    (i) => i.placement === 'both' || i.placement === 'drawer' || i.placement === 'cta'
  );

  const ctaItem = navItems.find((i) => i.placement === 'cta') || null;

  const drawer = (
    <>
      <DrawerHeader>
        <DrawerLogoLink to="/" onClick={handleNavClick} aria-label={t(A11Y.homeLink, language)}>
          <DrawerLogo src={logo} alt="Soquelec" draggable="false" />
        </DrawerLogoLink>
        <DrawerCloseButton onClick={handleDrawerToggle} aria-label={t(A11Y.closeNavigation, language)}>
          <CloseRoundedIcon />
        </DrawerCloseButton>
      </DrawerHeader>

      <DrawerNavList>
        {/* The home route is represented by the logo in the drawer
            header, so it is simply absent from the nav list rather than
            sliced off by index. */}
        {drawerItems.map((item) => (
          <DrawerItem key={item.route} component={NavLink} to={item.route} onClick={handleNavClick}>
            {label(item)}
          </DrawerItem>
        ))}
      </DrawerNavList>

      <DrawerFooter>
        <DrawerLangButton onClick={toggleLanguage}>
          {language === "EN" ? "Français" : "English"}
        </DrawerLangButton>
      </DrawerFooter>
    </>
  );

  return (
    <StyledAppBar position="fixed" $hidden={hidden} $scrolled={!atTop}>
      <StyledToolbar>
        <NavLink to="/">
          <StyledLogo src={logo} alt="Soquelec Logo" />
        </NavLink>

        {isMobile ? (
          <NavActions $mobile>
            {ctaItem && (
              <MobileCartLink to={ctaItem.route}>
                <StyledIconButton edge="end" aria-label={label(ctaItem)} tabIndex={-1}>
                  <StyledBadge badgeContent={itemsInCartCount}>
                    <AssignmentRoundedIcon sx={{ fontSize: { xs: "1.5rem", sm: "1.7rem" }, color: color.brandPurple }} />
                  </StyledBadge>
                </StyledIconButton>
              </MobileCartLink>
            )}
            <StyledIconButton edge="end" aria-label={t(A11Y.openNavigation, language)} onClick={handleDrawerToggle}>
              <MenuRoundedIcon sx={{ fontSize: { xs: "1.5rem", sm: "1.7rem" }, color: color.brandPurple }} />
            </StyledIconButton>
          </NavActions>
        ) : (
          <>
            <NavActions>
              {headerItems.map((item) => (
                <NavLink key={item.route} to={item.route}>{label(item)}</NavLink>
              ))}
            </NavActions>
            {/* Label comes from the same nav item that supplies the
                route, so the button and the drawer entry can no longer
                disagree — previously this string was hardcoded here as
                well as living in the array. */}
            {ctaItem && (
              <CartLink to={ctaItem.route}>
                <StyledBadge badgeContent={itemsInCartCount} color="primary">
                  <CartButton>{label(ctaItem)}</CartButton>
                </StyledBadge>
              </CartLink>
            )}
          </>
        )}

        <StyledDrawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          /* Skip MUI's default focus-restore on close — combined with
             the manual blur in handleNavClick, this prevents the brief
             focus flash on the menu trigger before navigation. */
          disableRestoreFocus
        >
          {drawer}
        </StyledDrawer>
      </StyledToolbar>
    </StyledAppBar>
  );
}
